import { InvoiceData, InvoiceItem } from "../types/InvoiceData";
import OpsDateUtils from "./OpsDateUtils";

// Percentage, fixed for now: not editable by the user, but may become variable
export const TAX_RATE = 0;

export const AVAILABLE_CURRENCIES = ["USD"];

export const BILL_TO_DEFAULT_DATA = `OOTB Labs Inc.
2875 NE 191ST
Suite 901
Aventura, FL 33180, USA`;

export const DEFAULT_CURRENCY = AVAILABLE_CURRENCIES[0];

export const DEFAULT_ITEM_QUANTITY = 1;

/**
 * Calendar day in the browser timezone, same reading the date picker uses.
 * `new Date("YYYY-MM-DD")` is UTC midnight and shifts to the previous day west of UTC.
 */
export function parseInvoiceIssueDate(iso: string): Date | null {
  const match = iso?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  // Month 0 and 13 roll into another year, and day 0 or 32 rolls into another month.
  const previousMonth = month - 1;
  const date = new Date(year, previousMonth, day);
  if (date.getFullYear() !== year || date.getMonth() !== previousMonth || date.getDate() !== day) return null;
  return date;
}

export function formatInvoiceIssueDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** True when prefill would replace a beneficiary, a line item, or a custom bill-to. */
export function prefillReplacesExistingData(data: InvoiceData): boolean {
  if (data.from.beneficiaryName.trim() || data.from.country.trim()) return true;
  if (data.billTo.trim() && data.billTo !== BILL_TO_DEFAULT_DATA) return true;
  if (data.items.length !== 1) return true;
  const item = data.items[0];
  if (!item) return false;
  return item.description.trim() !== "" || item.quantity !== DEFAULT_ITEM_QUANTITY;
}

export default class InvoiceUtils {
  static calculateTotals(items: InvoiceItem[]): { subtotal: number; taxRate: number; taxAmount: number; total: number } {
    const subtotal = items.reduce((acc, item) => acc + item.quantity * item.unitPrice, 0);
    const taxAmount = subtotal * (TAX_RATE / 100);
    return { subtotal, taxRate: TAX_RATE, taxAmount, total: subtotal + taxAmount };
  }

  static getLastBusinessDayOfMonth(year: number, month: number): string {
    const lastDay = new Date(year, month + 1, 0);

    while (OpsDateUtils.isWeekend(lastDay)) {
      lastDay.setDate(lastDay.getDate() - 1);
    }

    const y = lastDay.getFullYear();
    const m = String(lastDay.getMonth() + 1).padStart(2, "0");
    const d = String(lastDay.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  static formatCurrency(amount: number, currency: string): string {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
  }

  static formatDateForDisplay(isoDate: string): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const [year, month, day] = isoDate.split("-");
    return `${months[Number(month) - 1]} ${Number(day)}, ${year}`;
  }
}
